import { FC, ReactNode, useState } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbEdit, TbExternalLink, TbFolderSymlink, TbLink, TbStar, TbStarOff, TbTrash } from 'react-icons/tb';

import { useWorkspaceResourceAccess } from '@/hooks/useWorkspaceResourceAccess';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import RenameResourceModal from '@/shared/components/modals/ResourceActionModals/01-RenameResourceModal';
import MoveResourceToFolderModal from '@/shared/components/modals/ResourceActionModals/02-MoveResourceToFolderModal';
import DeleteResourceModal from '@/shared/components/modals/ResourceActionModals/03-DeleteResourceModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    getWorkspaceResourceStatusToggleLabel,
    WorkspaceResourceStatusActionIcon,
} from '@/shared/components/workspace-resources';
import { usePipelineStore } from '@/stores';
import { Pipeline } from '@/types';

import { getWorkspaceResourceRemovalActionLabel, WorkspaceResourceActionMenuItem } from '../helpers';
import WorkspaceResourceActionsDropdown from '../WorkspaceResourceActionsDropdown';

interface PipelinesActionsDropdownProps {
    pipeline: Pipeline;
    children: ReactNode;
    onOpenChange?: (open: boolean) => void;
    open?: boolean;
}

const PipelinesActionsDropdown: FC<PipelinesActionsDropdownProps> = ({
    pipeline,
    children,
    onOpenChange,
    open,
}): JSX.Element => {
    // const [templatized, setTemplatized] = useState(pipeline?.isTemplate);

    const { pipelines, togglePipelineFavorite, duplicatePipeline, togglePipelineArchive, togglePipelineLock } =
        usePipelineStore();
    const access = useWorkspaceResourceAccess('pipelines', pipeline?.id);
    const createAccess = useWorkspaceResourceCreateAccess('pipelines', pipelines?.length ?? 0);

    const handlePipelineFavoriteToggle = async () => {
        togglePipelineFavorite(pipeline?.id, !pipeline?.isFavorite);
    };

    const handlePipelineFavoriteLock = async () => {
        togglePipelineLock(pipeline?.id, !pipeline?.isLocked);
    };

    const handlePipelineFavoriteArchive = async () => {
        togglePipelineArchive(pipeline?.id, !pipeline?.isArchived);
    };

    const handleDuplicatePipeline = async () => {
        if (createAccess.disabled) {
            return;
        }

        duplicatePipeline(pipeline?.id);
    };

    const [renameVisible, setRenameVisible] = useState<boolean>(false);
    const [moveToFolderVisible, setMoveToFolderVisible] = useState(false);
    const [deleteVisible, setDeleteVisible] = useState<boolean>(false);

    const items: WorkspaceResourceActionMenuItem[] = [
        {
            key: 'favorited',
            icon: pipeline?.isFavorite ? <TbStarOff size={'1rem'} /> : <TbStar size={'1rem'} />,
            label: pipeline?.isFavorite ? 'Remove from Favorites' : 'Add to Favorites',
            onClick: handlePipelineFavoriteToggle,
            permission: 'update',
        },
        {
            key: 'locked',
            icon: <WorkspaceResourceStatusActionIcon active={pipeline?.isLocked} status='locked' />,
            label: getWorkspaceResourceStatusToggleLabel('locked', pipeline?.isLocked),
            onClick: handlePipelineFavoriteLock,
            permission: 'manage',
        },
        {
            key: 'archived',
            icon: <WorkspaceResourceStatusActionIcon active={pipeline?.isArchived} status='archived' />,
            label: getWorkspaceResourceStatusToggleLabel('archived', pipeline?.isArchived),
            onClick: handlePipelineFavoriteArchive,
            permission: 'manage',
        },
        {
            key: 'copyLink',
            icon: <TbLink size={'1rem'} />,
            label: 'Copy Link',
            onClick: () => {
                navigator.clipboard
                    .writeText(
                        `${
                            import.meta.env.DEV ? 'http://localhost:3000' : 'https://dashboard.countercyclical.io'
                        }/pipelines/${pipeline?.id}`,
                    )
                    .then(
                        () => {
                            toast('Link Copied', {
                                icon: <TbLink size='1.125rem' style={{ marginRight: '0.5rem' }} />,
                                description: pipeline?.name,
                            });
                        },
                        (error) => {
                            toast('Error Copying Link', {
                                description: pipeline?.name,
                            });

                            captureException(error);
                        },
                    );
            },
            permission: 'read',
        },
        {
            key: 'openInNewWindow',
            icon: <TbExternalLink size={'1rem'} />,
            label: 'Open in New Window',
            onClick: () => {
                window.open(`/pipelines/${pipeline?.id}`, '_blank');
            },
            permission: 'read',
        },
        {
            key: 'rename',
            icon: <TbEdit size={'1rem'} />,
            label: 'Rename',
            onClick: () => {
                setRenameVisible(true);
            },
            permission: 'update',
        },
        {
            key: 'duplicate',
            disabled: createAccess.disabled,
            icon: <BiDuplicate size={'1rem'} />,
            label: 'Duplicate',
            onClick: handleDuplicatePipeline,
            permission: 'read',
        },
        // {
        //     key: 'templatized',
        //     icon: templatized ? <TbIcons size={'1rem'} /> : <TbIconsOff size={'1rem'} />,
        //     label: templatized ? 'Revert Template' : 'Convert to Template',
        //     onClick: () => {
        //         authorizedAxiosInstance
        //             .put(`/api/pipelines/${pipeline?.id}`, {
        //                 isTemplate: !templatized,
        //             })
        //             .then((res: AxiosResponse) => {
        //                 if (res.status === 200) {
        //                     setArchived(res?.data?.isTemplate);

        //                     toast(`Pipeline ${archived ? 'Template Reverted' : 'Converted to Template'}`, {
        //                         icon: res?.data?.isTemplate ? <TbIconsOff size={'1rem'} /> : <TbIcons size={'1rem'} />,
        //                         description: res?.data?.editedName,
        //                     });
        //                 }
        //             });
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'transfer',
        //     icon: <TbArrowsHorizontal size="1rem" />,
        //     label: 'Transfer',
        //     onClick: () => {
        //         return;
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'export',
        //     icon: <TbFileExport size={'1rem'} style={{ transform: 'rotate(-90deg)' }} />,
        //     label: 'Export',
        //     onClick: () => {
        //         return;
        //     },
        //     disabled: true,
        // },
        {
            key: 'moveToFolder',
            icon: <TbFolderSymlink size='1rem' />,
            label: 'Move to Folder',
            onClick: () => {
                setMoveToFolderVisible(true);
            },
            permission: 'update',
        },
        {
            key: 'delete',
            icon: <TbTrash size={'1rem'} />,
            label: getWorkspaceResourceRemovalActionLabel('pipelines'),
            onClick: () => {
                setDeleteVisible(true);
            },
            danger: true,
            permission: 'manage',
        },
    ];

    return (
        <>
            <WorkspaceResourceActionsDropdown
                access={access}
                history={{ resourceId: pipeline.id, resourceType: 'pipelines', title: pipeline.name }}
                items={items}
                onOpenChange={onOpenChange}
                open={open}
                resourceLabel='Pipeline'
            >
                {children}
            </WorkspaceResourceActionsDropdown>
            {/* Action Modals */}
            <RenameResourceModal
                resource={pipeline}
                singularResource={'pipeline'}
                open={renameVisible}
                setOpen={setRenameVisible}
            />
            <MoveResourceToFolderModal
                open={moveToFolderVisible}
                setOpen={setMoveToFolderVisible}
                resource={pipeline}
                singularResource='pipeline'
            />
            <DeleteResourceModal
                resource={pipeline}
                singularResource={'pipeline'}
                open={deleteVisible}
                setOpen={setDeleteVisible}
            />
        </>
    );
};

export default PipelinesActionsDropdown;
